import { findDictionaryFindings } from "./dictionary.js";
import { resolveOverlaps } from "./overlap.js";
import { isExactRuleRef, rule } from "./rules.js";
import { ibanLengthForCountry, ibanValid, luhnValid, validateDanishCpr, validateLithuanianPersonalCode, validateSwedishIdentity } from "./validators.js";
import { InputLimitError, ScanCancelledError, type Country, type DetectionOptions, type DetectionResult, type Finding } from "./types.js";

const DEFAULT_MAX_INPUT = 250_000;

function check(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new ScanCancelledError();
}

function enabled(options: DetectionOptions, country: Country): boolean {
  return options.countries === undefined || options.countries.includes(country);
}

function makeFinding(input: Omit<Finding, "id"> & { id?: string }): Finding {
  return { ...input, id: input.id ?? `${input.rule.id}@${input.rule.version}:${input.startUtf16}:${input.endUtf16}` };
}

function capture(input: string, expression: RegExp, group: number, make: (start: number, end: number, value: string) => Finding | undefined, signal?: AbortSignal): Finding[] {
  const findings: Finding[] = [];
  let match: RegExpExecArray | null;
  let iterations = 0;
  while ((match = expression.exec(input)) !== null) {
    if ((iterations++ & 127) === 0) check(signal);
    const value = match[group];
    if (value === undefined) continue;
    // Every current detector captures the sensitive value at the tail of its
    // match. lastIndexOf also handles assignment rules where group 2 follows a
    // quoted group 1, without relying on the newer RegExp `d` flag.
    const start = match.index + (match[0]!.lastIndexOf(value));
    const result = make(start, start + value.length, value);
    if (result) findings.push(result);
    if (match[0]!.length === 0) expression.lastIndex += 1;
  }
  return findings;
}

function emails(input: string, signal?: AbortSignal): Finding[] {
  // Bounded local/domain labels avoid untrusted nested quantifiers.
  return capture(input, /(?:^|[^\w.+-])([A-Za-z0-9][A-Za-z0-9._%+-]{0,62}@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+)/g, 1, (start, end) => makeFinding({
    startUtf16: start, endUtf16: end, rawType: "email", displayType: "Email", category: "personal", severity: "warning", confidence: 0.95, status: "candidate", source: "rule", rule: rule("email_syntax"), country: "core", validation: ["syntax"],
  }), signal);
}

function ibans(input: string, signal?: AbortSignal): Finding[] {
  const findings: Finding[] = [];
  const starts = /[A-Z]{2}\d{2}/gi;
  let match: RegExpExecArray | null;
  while ((match = starts.exec(input)) !== null) {
    check(signal);
    const start = match.index;
    if (start > 0 && /[A-Za-z0-9]/.test(input[start - 1]!)) continue;
    const expectedLength = ibanLengthForCountry(match[0]!.slice(0, 2));
    if (!expectedLength) continue;

    let cursor = start;
    let compactLength = 0;
    let end = start;
    while (cursor < input.length && compactLength < expectedLength) {
      const character = input[cursor]!;
      if (/[A-Za-z0-9]/.test(character)) {
        compactLength += 1;
        end = cursor + 1;
        cursor += 1;
      } else if ((character === " " || character === "-") && compactLength >= 4) {
        cursor += 1;
      } else {
        break;
      }
    }
    if (compactLength !== expectedLength) continue;
    if (end < input.length && /[A-Za-z0-9]/.test(input[end]!)) continue;
    const value = input.slice(start, end);
    if (!ibanValid(value)) continue;
    findings.push(makeFinding({
      startUtf16: start, endUtf16: end, rawType: "iban", displayType: "IBAN", category: "personal", severity: "critical", confidence: 1, status: "validated", source: "rule", rule: rule("iban_mod97"), country: "core", validation: ["country_length", "mod97"],
    }));
    starts.lastIndex = end;
  }
  return findings;
}

function cards(input: string, signal?: AbortSignal): Finding[] {
  return capture(input, /(?:^|[^\d])((?:\d[ -]?){12,18}\d)(?!\d)/g, 1, (start, end, value) => {
    const compact = value.replace(/[ -]/g, "");
    if (compact.length < 13 || compact.length > 19 || !luhnValid(compact)) return undefined;
    return makeFinding({ startUtf16: start, endUtf16: end, rawType: "payment_card", displayType: "Payment card", category: "personal", severity: "critical", confidence: 1, status: "validated", source: "rule", rule: rule("payment_card_luhn"), country: "core", validation: ["luhn"] });
  }, signal);
}

function secrets(input: string, signal?: AbortSignal): Finding[] {
  const result: Finding[] = [];
  // A final punctuation mark belongs to prose more often than to a bearer
  // token. Dots remain allowed inside a token but not as its last character.
  result.push(...capture(input, /\bBearer[ \t]+([A-Za-z0-9_~+/=-](?:[A-Za-z0-9._~+/=-]{14,}[A-Za-z0-9_~+/=-]))/g, 1, (start, end) => makeFinding({
    startUtf16: start, endUtf16: end, rawType: "bearer_token", displayType: "Bearer token", category: "secret", severity: "critical", confidence: 0.99, status: "validated", source: "rule", rule: rule("bearer_token"), country: "core", validation: ["bearer_prefix"],
  }), signal));
  result.push(...capture(input, /\b(eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,})\b/g, 1, (start, end) => makeFinding({
    startUtf16: start, endUtf16: end, rawType: "jwt", displayType: "JWT", category: "secret", severity: "critical", confidence: 0.92, status: "candidate", source: "rule", rule: rule("jwt_shape"), country: "core", validation: ["three_segments"],
  }), signal));
  result.push(...capture(input, /\b(?:password|passwd|pwd|secret|api[_-]?key|access[_-]?token)\s*(?:=|:)\s*(["']?)([^\s"']{6,})/gi, 2, (start, end) => makeFinding({
    startUtf16: start, endUtf16: end, rawType: "assigned_secret", displayType: "Assigned secret", category: "secret", severity: "critical", confidence: 0.9, status: "candidate", source: "rule", rule: rule("secret_assignment"), country: "core", validation: ["assignment_context"],
  }), signal));

  // Manual bounded scan avoids an unbounded dot-star private-key regex.
  const begin = "-----BEGIN ";
  let cursor = 0;
  while (cursor < input.length) {
    check(signal);
    const start = input.indexOf(begin, cursor);
    if (start < 0) break;
    const headerEnd = input.indexOf("-----", start + begin.length);
    if (headerEnd < 0 || headerEnd - start > 96) { cursor = start + begin.length; continue; }
    const header = input.slice(start, headerEnd + 5);
    const headerMatch = /^-----BEGIN (?:(?<label>[A-Z0-9 ]+) )?PRIVATE KEY-----$/.exec(header);
    if (!headerMatch) { cursor = start + begin.length; continue; }
    const label = headerMatch.groups?.label ?? "";
    const footer = `-----END ${label ? `${label} ` : ""}PRIVATE KEY-----`;
    const endStart = input.indexOf(footer, headerEnd + 5);
    if (endStart >= 0 && endStart - start <= 65_536) {
      const end = endStart + footer.length;
      result.push(makeFinding({ startUtf16: start, endUtf16: end, rawType: "private_key", displayType: "Private key", category: "secret", severity: "critical", confidence: 1, status: "validated", source: "rule", rule: rule("pem_private_key"), country: "core", validation: ["pem_header_footer"] }));
      cursor = end;
    } else cursor = start + begin.length;
  }
  return result;
}

function swedishIdentities(input: string, signal?: AbortSignal, referenceDate?: string): Finding[] {
  return capture(input, /(?:^|[^\d])((?:\d{2}|\d{4})\d{2}\d{2}[-+]?\d{4})(?!\d)/g, 1, (start, end, value) => {
    const validation = validateSwedishIdentity(value, referenceDate);
    if (!validation.valid) return undefined;
    const rawType = validation.coordination ? "se_samordningsnummer" : "se_personnummer";
    const findingRule = validation.coordination ? rule("se_samordningsnummer") : rule("se_personnummer");
    return makeFinding({ startUtf16: start, endUtf16: end, rawType, displayType: validation.coordination ? "Swedish coordination number" : "Swedish personnummer", category: "personal", severity: "critical", confidence: 1, status: "validated", source: "rule", rule: findingRule, country: "se", validation: validation.validation });
  }, signal);
}

function danishCpr(input: string, signal?: AbortSignal): Finding[] {
  return capture(input, /(?:^|[^\d])(\d{6}(?:[- ]?\d{4}))(?!\d)/g, 1, (start, end, value) => {
    const validation = validateDanishCpr(value);
    if (!validation.validDate) return undefined;
    const separated = /[- ]/.test(value);
    // Date structure and the historical Modulus 11 check can only support a
    // review candidate. Neither proves that a CPR is issued or currently valid.
    const status = "candidate";
    return makeFinding({
      startUtf16: start, endUtf16: end, rawType: "dk_cpr", displayType: "Danish CPR", category: "personal", severity: "critical", confidence: validation.mod11 ? 0.98 : separated ? 0.9 : 0.72, status, source: "rule", rule: rule("dk_cpr_date"), country: "dk", validation: validation.mod11 ? ["date", "mod11"] : ["date"],
    });
  }, signal);
}

function lithuanianCodes(input: string, signal?: AbortSignal): Finding[] {
  return capture(input, /(?:^|[^\d])(\d{11})(?!\d)/g, 1, (start, end, value) => validateLithuanianPersonalCode(value) ? makeFinding({
    startUtf16: start, endUtf16: end, rawType: "lt_personal_code", displayType: "Lithuanian personal code", category: "personal", severity: "critical", confidence: 1, status: "validated", source: "rule", rule: rule("lt_personal_code"), country: "lt", validation: ["date", "checksum"],
  }) : undefined, signal);
}

/** Pure, local text scanner. It neither persists nor logs input. */
export function detectText(input: string, options: DetectionOptions = {}): DetectionResult {
  const maximum = options.maxInputLength ?? DEFAULT_MAX_INPUT;
  if (input.length > maximum) throw new InputLimitError();
  check(options.signal);
  const candidates: Finding[] = [];
  candidates.push(...findDictionaryFindings(input, options.dictionary ?? []));
  check(options.signal);
  candidates.push(...emails(input, options.signal), ...ibans(input, options.signal), ...cards(input, options.signal), ...secrets(input, options.signal));
  if (enabled(options, "se")) candidates.push(...swedishIdentities(input, options.signal, options.referenceDate));
  if (enabled(options, "dk")) candidates.push(...danishCpr(input, options.signal));
  if (enabled(options, "lt")) candidates.push(...lithuanianCodes(input, options.signal));
  check(options.signal);
  const exactRuleCandidates = options.enabledRules === undefined
    ? candidates
    : candidates.filter((finding) => options.enabledRules!.some((enabledRule) => isExactRuleRef(enabledRule, finding.rule)));
  return resolveOverlaps(exactRuleCandidates);
}
